import {
  Building2,
  CalendarCheck2,
  CalendarRange,
  ChartNoAxesCombined,
  House,
  MessageCircle,
  SprayCan,
} from 'lucide-react';
import { PRIVILEGES } from '@stayon/auth-core';
import { ROLE_GROUPS, ROLES } from '@/shared/auth';
import {
  AVAILABILITY_DISTRIBUTION_SURFACE,
  CLEANING_QUEUE_SURFACE,
  DASHBOARD_SURFACE,
} from '@/shared/auth/privilegeSurfaces';
import type { NavArea } from './adminNavigationTypes';

export type { NavArea, NavCountKey, NavEntry, NavSearch } from './adminNavigationTypes';
export { accountEntries, platformEntries, settingsEntry } from './shellEntries';

const { workspaceAdmin, staff, staffOwner } = ROLE_GROUPS;
const under = (base: string) => (path: string) => path.startsWith(`${base}/`);

/**
 * Die sieben Bereiche der Seitenleiste (Board „Gerüst“). Jeder Eintrag trägt dieselbe
 * Rolle oder dasselbe Recht wie die Route, auf die er zeigt; neu ist nur, wo er steht. Einzige
 * gewollte Abweichung: Eigentümer sehen die Preisautomatik nicht mehr (PO-Entscheid 2026-10-19).
 */
export const shellAreas: NavArea[] = [
  {
    id: 'heute',
    label: 'Heute',
    icon: House,
    entries: [
      { to: DASHBOARD_SURFACE.route, label: 'Heute', privilege: DASHBOARD_SURFACE.privilege },
    ],
  },
  {
    id: 'kalender',
    label: 'Kalender',
    icon: CalendarRange,
    entries: [
      { to: '/calendar', label: 'Belegung', roles: staffOwner },
      {
        to: AVAILABILITY_DISTRIBUTION_SURFACE.route,
        label: 'Verfügbarkeit & Vertrieb',
        privilege: AVAILABILITY_DISTRIBUTION_SURFACE.privilege,
      },
      {
        to: '/dynamic-pricing',
        label: 'Preisautomatik',
        roles: staff,
        matches: under('/dynamic-pricing'),
      },
    ],
  },
  {
    id: 'buchungen',
    label: 'Buchungen',
    icon: CalendarCheck2,
    entries: [
      {
        to: '/bookings',
        label: 'Aktuell & kommend',
        roles: staffOwner,
        matches: (path) => under('/bookings')(path) && !path.startsWith('/bookings/quarantine'),
      },
      {
        to: '/bookings/quarantine',
        label: 'Nicht zugeordnet',
        roles: staff,
        count: 'revisionHolds',
      },
      { to: '/system/onboarding-links', label: 'Gastzugänge', roles: workspaceAdmin },
    ],
  },
  {
    id: 'nachrichten',
    label: 'Nachrichten',
    icon: MessageCircle,
    count: 'inboxAwaitingReply',
    entries: [{ to: '/inbox', label: 'Nachrichten', roles: staff, matches: under('/inbox') }],
  },
  {
    id: 'reinigung',
    label: 'Reinigung',
    icon: SprayCan,
    entries: [
      {
        to: CLEANING_QUEUE_SURFACE.route,
        label: 'Aufgaben',
        privilege: CLEANING_QUEUE_SURFACE.privilege,
        matches: (path) => under('/cleaning')(path) && !path.startsWith('/cleaning/reports'),
      },
      {
        to: CLEANING_QUEUE_SURFACE.route,
        label: 'Woche',
        search: { view: 'calendar' },
        privilege: CLEANING_QUEUE_SURFACE.privilege,
      },
      { to: '/cleaning/reports', label: 'Auswertung', privilege: PRIVILEGES.cleaningReportsView },
      {
        to: '/cleaning-teams',
        label: 'Teams',
        privilege: PRIVILEGES.cleaningTeamsManage,
        // Mitarbeiter tragen das Recht im Preset, sahen die Teams aber nie im Menü (nur im Hub der
        // Verwaltung); so bleibt es.
        requireRoles: [ROLES.WorkspaceAdmin, ROLES.HousekeepingManager],
      },
    ],
  },
  {
    id: 'objekte',
    label: 'Objekte',
    icon: Building2,
    entries: [
      {
        to: '/property-groups',
        label: 'Häuser',
        privilege: PRIVILEGES.propertyGroupsView,
        matches: under('/property-groups'),
      },
      { to: '/properties', label: 'Apartments', roles: staffOwner, matches: under('/properties') },
      { to: '/akte', label: 'Akte', roles: staff, matches: under('/akte') },
      { to: '/parking', label: 'Parkplätze', roles: staff },
    ],
  },
  {
    id: 'finanzen',
    label: 'Finanzen',
    icon: ChartNoAxesCombined,
    entries: [
      { to: '/finance', label: 'Übersicht', roles: staffOwner },
      { to: '/invoices', label: 'Rechnungen', roles: staff, matches: under('/invoices') },
      { to: '/finance/automation', label: 'Prüffälle', roles: staff },
    ],
  },
];
